<script setup lang="ts">
import type { ReportFilters } from '~/models/ReportFilters'
import type { UserAssignmentData } from '~/models/Report'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons'
import { resolveUserName } from '~/utils/reportUtils'
import { PendoFeature } from '~/models/Pendo'
import { useTime } from '~/composables/useTime'
import useFormWriter from '~/composables/useFormWriter'
import useGroupsStore from '~/stores/groups'

const props = defineProps<{ userAssignment: UserAssignmentData, assignmentId: string, filters?: ReportFilters }>()
const emit = defineEmits<{ click: [] }>()

const { t } = useI18n()
const { formatReportTimestamp } = useTime()
const { data: _data, isLoadingFormData: _isLoadingFormData, isError: _isError } = useFormWriter(Number(props.assignmentId.slice('form_writer-'.length)))

const relatedGroupMember = computed(() => {
  if (!props.userAssignment.userId || !props.filters?.selectedGroup?.groupId) {
    return undefined
  }
  const groupsStore = useGroupsStore()
  return groupsStore.findGroupMembersById(props.filters.selectedGroup.groupId)
    .find(member => member.userId === props.userAssignment.userId)
})

const studentName = computed(() => resolveUserName(relatedGroupMember.value))
const hasAssignment = computed(() => !!props.userAssignment.lastActivity || !!props.assignmentId)
const isAssignmentAnswered = computed(() => !!(props.userAssignment.data && props.userAssignment.data.trim().length > 0))
const formattedTimestamp = computed(() => formatReportTimestamp(props.userAssignment.lastActivity) || null)
const displayTimestamp = computed(() => formattedTimestamp.value ? `${t('reports.tasks.lastChanged')}: ${formattedTimestamp.value}` : '\u00A0')
const displayStatus = computed(() => isAssignmentAnswered.value ? `${t('reports.formwriter.delivered')}:` : t('reports.formwriter.notDelivered'))
</script>

<template>
  <button
    type="button"
    class="group border-green-40 flex w-full items-stretch overflow-hidden rounded-lg border bg-white text-left transition-all duration-200 focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-offset-2"
    :class="[
      isAssignmentAnswered
        ? 'hover:bg-gray-2 hover:border-green-30 hover:shadow-[0_0_0_1px_theme(colors.green.30)] cursor-pointer'
        : 'cursor-default',
    ]"
    :data-pendo="PendoFeature.ActivityReports.AssignmentRow"
    :disabled="!isAssignmentAnswered"
    @click="isAssignmentAnswered && emit('click')"
  >
    <div class="flex flex-1 flex-col gap-2 p-3 transition md:flex-row md:items-center md:gap-0 md:p-5">
      <div class="border-green-10 min-w-0 flex-1 border-b pb-2 md:border-0 md:pr-4 md:pb-0">
        <div class="text-green-40 min-h-5 text-base font-medium">
          <p class="break-words" v-text="studentName"></p>
        </div>
        <div class="text-green-40 mt-1 text-sm">
          <p class="break-words" v-text="displayTimestamp"></p>
        </div>
      </div>

      <div
        v-if="hasAssignment"
        class="flex w-24 items-center md:pl-4"
        :title="isAssignmentAnswered ? t('reports.formwriter.delivered') : t('reports.formwriter.notDelivered')"
      >
        <span class="text-green-40 text-sm font-medium" v-text="displayStatus"></span>
        <FontAwesomeIcon
          v-if="isAssignmentAnswered"
          :icon="faCircleCheck"
          class="text-green-40 ml-2 h-6 w-6 text-xl md:h-8 md:w-8 md:text-2xl"
          :title="t('reports.formwriter.delivered')"
          aria-hidden="true"
        />
        <span class="sr-only">{{ isAssignmentAnswered ? t('reports.formwriter.delivered') : '' }}</span>
      </div>
    </div>
    <div
      class="flex w-8 items-center justify-center self-stretch transition-colors duration-200"
      :class="[
        isAssignmentAnswered ? 'bg-green-40 group-hover:bg-green-30' : 'bg-gray-20',
      ]"
    >
      <svg
        class="h-12 w-12"
        :class="[
          isAssignmentAnswered ? 'text-white' : 'text-gray-60',
        ]"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M9 5l7 7-7 7"
        />
      </svg>
    </div>
  </button>
</template>
